import React, { useContext } from 'react';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { typeStyle } from '@carvana/showroom-css-theme';
import 'react-slidedown/lib/slidedown.css';
import { Warranty } from '@components/service-and-repair/constants';
import { AnalyticEvents } from '../../constants';
import styles from './vehicle-coverage.module.css';
import VehicleCoverageIcon from '../../assets/vehicle-coverage.svg';
import { ManufacturerWarranty } from './manufacturer-warranty';
import { LimitedWarranty } from './limited-warranty';
import { CarvanaCare } from './carvana-care';

export type VehicleCoverageProps = {
  feedData: WarrantyFeedData;
  positionValue?: number;
  userState?: any;
};

export const VehicleCoverage = ({ feedData, positionValue, userState }: VehicleCoverageProps) => {
  const { trackEvent } = useContext(AnalyticsContext) as any;
  const coverage = feedData?.content?.coverage || [];

  const mfgWarranties: any = coverage?.filter(
    (warrantyInfo) => warrantyInfo.type === Warranty.OEMBasic || warrantyInfo.type === Warranty.OEMPowerTrain
  );
  const ltdWarranty: any = coverage?.find((warrantyInfo) => warrantyInfo.type === Warranty.LimitedWarranty);
  const ccWarranty: any = coverage?.find((warrantyInfo) => warrantyInfo.type.toLowerCase().startsWith('carvanacare'));

  const captureImpression = (customAttributes: any) => {
    trackEvent(AnalyticEvents.viewedOvpCardImpression, {
      cardId: feedData.id,
      cardPosition: positionValue,
      ...customAttributes,
    });
    datadogRum.addAction(AnalyticEvents.viewedOvpCardImpression, {
      cardId: feedData.id,
      cardPosition: positionValue,
      ...customAttributes,
    });
  };
  const trackEventHandler = (isCardOpen: boolean, type: string) => {
    trackEvent(isCardOpen ? AnalyticEvents.ovpCardExpand : AnalyticEvents.ovpCardCollapse, {
      cardId: feedData.id,
      cardPosition: positionValue,
      type,
    });
    datadogRum.addAction(isCardOpen ? AnalyticEvents.ovpCardExpand : AnalyticEvents.ovpCardCollapse, {
      cardId: feedData.id,
      cardPosition: positionValue,
      type,
    });
  };

  return (
    <div className={styles['vehicle-coverage']}>
      <div className={styles['vehicle-coverage-header']}>
        <img src={VehicleCoverageIcon} alt="vehicle coverage" />
        <div style={typeStyle('header-m')}>Vehicle Coverage</div>
      </div>

      <ManufacturerWarranty
        warranties={mfgWarranties}
        onToggle={(e) => trackEventHandler(e, 'manufacturer_warranty')}
        onIntersection={() => {
          captureImpression({
            type: [mfgWarranties?.[0]?.type.toLowerCase(), mfgWarranties?.[1]?.type.toLowerCase()],
            isExpired: [mfgWarranties?.[0]?.isExpired, mfgWarranties?.[1]?.isExpired],
          });
        }}
      />

      {!!ltdWarranty && (
        <LimitedWarranty
          warranty={ltdWarranty}
          onToggle={(e) => trackEventHandler(e, 'limited_warranty')}
          onIntersection={() => {
            captureImpression({
              type: [ltdWarranty?.type?.toLowerCase()],
              isExpired: [ltdWarranty?.isExpired],
            });
          }}
        />
      )}

      <CarvanaCare
        warranty={ccWarranty}
        onToggle={(e) => trackEventHandler(e, 'carvanaCare')}
        onIntersection={() => {
          captureImpression({
            type: ccWarranty?.type.toLowerCase(),
            isExpired: ccWarranty.isExpired,
          });
        }}
      />
    </div>
  );
};

export interface WarrantyData {
  type: Warranty;
  daysLeft?: number;
  daysLeftText?: string;
  deductible?: {
    inNetwork: number;
    outOfNetwork: number;
  };
  milesLeft?: number;
  totalCoverageInMiles?: number;
  monthsLeft?: number;
  monthsLeftText?: string;
  durationLeft?: string;
  isExpired?: boolean;
  brochureUrl?: string;
  // SERV-405: VCM mileage tracking fields (optional until backend supports them)
  purchaseMileage?: number;
  currentMileage?: number;
  mileageLastUpdated?: string;
}

export interface WarrantyFeedData {
  name: string;
  title: string;
  id: number;
  sortOrder: number;
  caption?: string;
  callToAction?: CallToAction;
  content: {
    coverage: WarrantyData[];
  };
}

export interface CallToAction {
  title: string;
  link: string;
}
